import React, { useState, useEffect } from 'react';
import { X, Bug, Lightbulb, Clock, User, Calendar, Edit2, Trash2, Send, MessageSquare } from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { RichTextContent } from '../ui/RichTextEditor';
import { Ticket, TicketComment, ticketPriorityLabels, ticketStatusLabels, ticketAreaLabels, ticketPriorityColors, ticketStatusColors } from '../../types/ticket';
import { getTicket, addComment, deleteComment, deleteTicket } from '../../services/ticketsApi';

interface TicketDetailProps {
  projectId: string;
  ticketId: string;
  onClose: () => void;
  onEdit: (ticket: Ticket) => void;
  onDelete: () => void;
  currentUserId: string;
}

export const TicketDetail: React.FC<TicketDetailProps> = ({ 
  projectId, 
  ticketId, 
  onClose, 
  onEdit, 
  onDelete,
  currentUserId 
}) => {
  const [ticket, setTicket] = useState<Ticket | null>(null);
  const [loading, setLoading] = useState(true);
  const [newComment, setNewComment] = useState('');
  const [submittingComment, setSubmittingComment] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const loadTicket = async () => {
    try {
      const data = await getTicket(projectId, ticketId);
      setTicket(data);
    } catch (err) {
      console.error('Error loading ticket:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTicket();
  }, [projectId, ticketId]);

  const handleAddComment = async () => {
    if (!newComment.trim() || submittingComment) return;

    setSubmittingComment(true);
    try {
      await addComment(projectId, ticketId, newComment.trim());
      setNewComment('');
      loadTicket(); // Reload to get updated comments
    } catch (err) {
      console.error('Error adding comment:', err);
    } finally {
      setSubmittingComment(false);
    }
  };

  const handleDeleteComment = async (commentId: string) => {
    if (!window.confirm('Tem certeza que deseja excluir este comentário?')) return;

    try {
      await deleteComment(projectId, ticketId, commentId);
      loadTicket();
    } catch (err) {
      console.error('Error deleting comment:', err);
    }
  };

  const handleDeleteTicket = async () => {
    if (!window.confirm('Tem certeza que deseja excluir este chamado? Esta ação não pode ser desfeita.')) return;

    setDeleting(true);
    try {
      await deleteTicket(projectId, ticketId);
      onDelete();
      onClose();
    } catch (err) {
      console.error('Error deleting ticket:', err);
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
        <Card variant="glass" className="p-8">
          <div className="animate-spin w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full" />
        </Card>
      </div>
    );
  }

  if (!ticket) {
    return (
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
        <Card variant="glass" className="p-8">
          <p className="text-foreground-secondary">Chamado não encontrado</p>
          <Button onClick={onClose} className="mt-4">Fechar</Button>
        </Card>
      </div>
    );
  }

  const TypeIcon = ticket.tipo === 'bug' ? Bug : Lightbulb;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <Card variant="glass" className="w-full max-w-3xl max-h-[90vh] flex flex-col">
        <CardHeader className="flex flex-row items-center justify-between shrink-0">
          <div className="flex items-center gap-3">
            <TypeIcon
              className={`w-6 h-6 ${ticket.tipo === 'bug' ? 'text-red-400' : 'text-yellow-400'}`}
            />
            <div>
              <CardTitle className="text-lg text-foreground-primary">
                {ticket.titulo}
              </CardTitle>
              <div className="flex items-center gap-2 mt-1">
                <span
                  className="px-2 py-0.5 text-xs font-medium rounded"
                  style={{
                    backgroundColor: `${ticketStatusColors[ticket.status]}20`,
                    color: ticketStatusColors[ticket.status]
                  }}
                >
                  {ticketStatusLabels[ticket.status]}
                </span>
                <span
                  className="px-2 py-0.5 text-xs rounded"
                  style={{
                    backgroundColor: `${ticketPriorityColors[ticket.prioridade]}20`,
                    color: ticketPriorityColors[ticket.prioridade]
                  }}
                >
                  {ticketPriorityLabels[ticket.prioridade]}
                </span>
                <span className="px-2 py-0.5 text-xs bg-white/5 rounded text-foreground-secondary">
                  {ticketAreaLabels[ticket.area]}
                </span>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => onEdit(ticket)}>
              <Edit2 className="w-4 h-4" />
            </Button>
            <Button variant="outline" size="sm" onClick={handleDeleteTicket} disabled={deleting}>
              <Trash2 className="w-4 h-4 text-red-400" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={onClose}
              className="text-foreground-tertiary hover:text-foreground-primary"
            >
              <X className="w-4 h-4" />
            </Button>
          </div>
        </CardHeader>

        <CardContent className="flex-1 overflow-y-auto space-y-6">
          {/* Description */}
          <div>
            <h3 className="text-sm font-medium text-foreground-secondary mb-2">Descrição</h3>
            <RichTextContent content={ticket.descricao} className="text-foreground-primary" />
          </div>

          {/* Metadata */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div>
              <span className="text-foreground-tertiary block mb-1">Autor</span>
              <span className="text-foreground-primary flex items-center gap-1">
                <User className="w-4 h-4" />
                {ticket.autorId?.name || 'Desconhecido'}
              </span>
            </div>
            {ticket.responsavelId && (
              <div>
                <span className="text-foreground-tertiary block mb-1">Responsável</span>
                <span className="text-foreground-primary flex items-center gap-1">
                  <User className="w-4 h-4" />
                  {ticket.responsavelId.name}
                </span>
              </div>
            )}
            {ticket.estimatedHours && (
              <div>
                <span className="text-foreground-tertiary block mb-1">Horas Estimadas</span>
                <span className="text-foreground-primary flex items-center gap-1">
                  <Clock className="w-4 h-4" />
                  {ticket.estimatedHours}h
                </span>
              </div>
            )}
            <div>
              <span className="text-foreground-tertiary block mb-1">Criado em</span>
              <span className="text-foreground-primary flex items-center gap-1">
                <Calendar className="w-4 h-4" />
                {new Date(ticket.createdAt).toLocaleDateString('pt-BR')}
              </span>
            </div>
          </div>

          {/* Service Order Link */}
          {ticket.serviceOrderId && typeof ticket.serviceOrderId === 'object' && (
            <div className="p-3 bg-background-tertiary rounded border border-white/10">
              <span className="text-foreground-tertiary text-sm">Vinculado à OS: </span>
              <span className="text-foreground-primary font-medium">
                {ticket.serviceOrderId.code} - {ticket.serviceOrderId.name}
              </span>
            </div>
          )}

          {/* Comments Section */}
          <div>
            <h3 className="text-sm font-medium text-foreground-secondary mb-3 flex items-center gap-2">
              <MessageSquare className="w-4 h-4" />
              Comentários ({ticket.comments?.length || 0})
            </h3>

            <div className="space-y-3">
              {ticket.comments?.map((comment) => (
                <div key={comment._id} className="bg-background-tertiary rounded p-3 border border-white/5">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-foreground-primary font-medium text-sm">
                        {comment.autorId?.name || 'Usuário'}
                      </span>
                      <span className="text-foreground-tertiary text-xs">
                        {new Date(comment.createdAt).toLocaleString('pt-BR')}
                      </span>
                    </div>
                    {comment.autorId?._id === currentUserId && (
                      <button
                        onClick={() => handleDeleteComment(comment._id)}
                        className="text-foreground-tertiary hover:text-red-400"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                  <p className="text-foreground-secondary text-sm whitespace-pre-wrap">
                    {comment.conteudo}
                  </p>
                </div>
              ))}

              {(!ticket.comments || ticket.comments.length === 0) && (
                <p className="text-foreground-tertiary text-sm text-center py-4">
                  Nenhum comentário ainda. Seja o primeiro!
                </p>
              )}
            </div>
          </div>

          {/* Add Comment */}
          <div className="pt-4 border-t border-white/10">
            <h3 className="text-sm font-medium text-foreground-secondary mb-2">Adicionar comentário</h3>
            <div className="flex gap-2">
              <textarea
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                placeholder="Escreva seu comentário..."
                className="flex-1 px-3 py-2 bg-input border border-border rounded-md text-sm text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 ring-offset-background resize-none transition-colors"
                rows={3}
                maxLength={3000}
              />
              <Button
                variant="gradient"
                onClick={handleAddComment}
                disabled={!newComment.trim() || submittingComment}
                className="self-end"
              >
                <Send className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
